/* Explore — Interactive map with OpenLayers (Festival edition) */
const ExplorePage = () => {
  const D = window.PA_DATA;
  const visibleParcours = D.parcours.filter(p => p.status !== "past");
  const visibleVenueIds = new Set(visibleParcours.flatMap(p => p.venueIds || []));
  const [view, setView] = useState("map"); // map | list
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [filters, setFilters] = useState({ commune:"all", parcours:"all", type:"all" });
  const [query, setQuery] = useState("");
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layerRef = useRef(null);
  const sourceRef = useRef(null);

  const colors = ["#FF3E8F","#7A5BFF","#FF7A2A","#F2E94A","#4BD18A","#4DB1FF","#C8B8FF","#E5453A"];

  // Filtered venues
  const venues = useMemo(() => D.venues.filter(v =>
    visibleVenueIds.has(v.id) &&
    (filters.commune === "all" || v.commune === filters.commune) &&
    (filters.type === "all" || v.type === filters.type) &&
    (query === "" || v.name.toLowerCase().includes(query.toLowerCase()) || v.commune.toLowerCase().includes(query.toLowerCase()))
  ), [filters, query, visibleVenueIds]);

  // Init the map ONCE
  useEffect(() => {
    if (!mapRef.current || !window.ol || mapInstance.current) return;

    sourceRef.current = new ol.source.Vector();
    const clusterSource = new ol.source.Cluster({
      distance: 50,
      minDistance: 30,
      source: sourceRef.current
    });
    const styleCache = {};
    layerRef.current = new ol.layer.Vector({
      source: clusterSource,
      style: (f) => {
        const feats = f.get("features");
        const size = feats.length;
        if (size === 1) {
          // Single venue — render the colored numbered pin
          const inner = feats[0];
          const idx = inner.get("idx");
          const color = inner.get("color");
          const isSel = inner.get("selected");
          return [
            new ol.style.Style({
              image: new ol.style.Circle({
                radius: isSel ? 20 : 16,
                fill: new ol.style.Fill({ color }),
                stroke: new ol.style.Stroke({ color: "#0F0F0F", width: isSel ? 3 : 2 })
              })
            }),
            new ol.style.Style({
              text: new ol.style.Text({
                text: String(idx),
                font: "700 13px Inter, sans-serif",
                fill: new ol.style.Fill({ color: "#0F0F0F" })
              })
            })
          ];
        }
        // Cluster style — festival pink puck with white count
        const cacheKey = "c-" + size;
        if (!styleCache[cacheKey]) {
          const r = Math.min(34, 18 + Math.log2(size) * 6);
          styleCache[cacheKey] = [
            new ol.style.Style({
              image: new ol.style.Circle({
                radius: r + 6,
                fill: new ol.style.Fill({ color: "rgba(255, 62, 143, 0.22)" })
              })
            }),
            new ol.style.Style({
              image: new ol.style.Circle({
                radius: r,
                fill: new ol.style.Fill({ color: "#FF3E8F" }),
                stroke: new ol.style.Stroke({ color: "#0F0F0F", width: 2 })
              }),
              text: new ol.style.Text({
                text: String(size),
                font: "900 16px 'Archivo Black', sans-serif",
                fill: new ol.style.Fill({ color: "#0F0F0F" })
              })
            })
          ];
        }
        return styleCache[cacheKey];
      }
    });

    const map = new ol.Map({
      target: mapRef.current,
      controls: ol.control.defaults.defaults({ attribution:true, zoom:true }),
      interactions: ol.interaction.defaults.defaults({ mouseWheelZoom:true }),
      layers: [
        window.baseTileLayer(),
        layerRef.current
      ],
      view: new ol.View({
        center: ol.proj.fromLonLat([5.87, 50.48]),
        zoom: 11
      })
    });

    map.on("click", (evt) => {
      const f = map.forEachFeatureAtPixel(evt.pixel, (feat) => feat);
      if (f) {
        const feats = f.get("features");
        // Cluster click → zoom in
        if (feats && feats.length > 1) {
          const ext = ol.extent.createEmpty();
          feats.forEach(ff => ol.extent.extend(ext, ff.getGeometry().getExtent()));
          map.getView().fit(ext, { padding:[60,60,60,60], duration:400, maxZoom: 16 });
          return;
        }
        const single = feats ? feats[0] : f;
        const v = single.get("venue");
        if (v) {
          setSelectedVenue(v);
          map.getView().animate({
            center: single.getGeometry().getCoordinates(),
            duration: 400
          });
        }
      } else {
        setSelectedVenue(null);
      }
    });
    map.on("pointermove", (evt) => {
      const hit = map.hasFeatureAtPixel(evt.pixel);
      map.getTargetElement().style.cursor = hit ? "pointer" : "";
    });

    mapInstance.current = map;

    // Boundary overlay for all parcours' admin communes
    const allAdminCommunes = [...new Set(
      visibleParcours.flatMap(p => p.adminCommunes || [])
    )];
    if (allAdminCommunes.length) {
      window.addCommuneBoundaryLayer(map, allAdminCommunes, {
        fill: "rgba(255, 62, 143, 0.06)",
        stroke: "#0F0F0F",
        width: 2,
        dash: [6, 4]
      });
    }

    // Initial size update — preview iframe sometimes mounts at 0×0
    setTimeout(() => map.updateSize(), 100);
    setTimeout(() => map.updateSize(), 500);

    const resizeObs = new ResizeObserver(() => map.updateSize());
    resizeObs.observe(mapRef.current);

    return () => { resizeObs.disconnect(); map.setTarget(undefined); mapInstance.current = null; };
  }, []);

  // Update features when filters change
  useEffect(() => {
    if (!sourceRef.current) return;
    sourceRef.current.clear();
    venues.forEach((v, i) => {
      sourceRef.current.addFeature(new ol.Feature({
        geometry: new ol.geom.Point(ol.proj.fromLonLat(v.lonlat)),
        venue: v,
        idx: i+1,
        color: colors[i % colors.length],
        selected: false
      }));
    });
  }, [venues]);

  // Highlight selected
  useEffect(() => {
    if (!sourceRef.current) return;
    sourceRef.current.getFeatures().forEach(f => {
      f.set("selected", selectedVenue && f.get("venue").id === selectedVenue.id);
    });
    if (selectedVenue && mapInstance.current) {
      mapInstance.current.getView().animate({
        center: ol.proj.fromLonLat(selectedVenue.lonlat),
        zoom: Math.max(mapInstance.current.getView().getZoom(), 12),
        duration: 500
      });
    }
  }, [selectedVenue]);

  const FilterPills = ({ items, value, onChange }) => (
    <div className="row gap-2 wrap">
      {items.map(it => (
        <button key={it.v} onClick={() => onChange(it.v)}
                className={"chip " + (value === it.v ? "chip-active" : "")}
                style={{padding:"5px 11px", fontSize:11}}>
          {it.l}
        </button>
      ))}
    </div>
  );

  const communeOptions = [{v:"all",l:"Toutes"}, ...D.communes.map(c => ({v:c.name,l:c.name}))];
  const typeOptions    = [{v:"all",l:"Tous"}, ...[...new Set(venues.map(v=>v.type))].map(t => ({v:t,l:t}))];

  return (
    <div data-screen-label="04 Carte" style={{position:"relative", paddingBottom:64}}>
      {/* HEADER */}
      <section className="container-w" style={{paddingTop:48, paddingBottom:32}}>
        <div className="row between wrap" style={{gap:24, alignItems:"flex-end"}}>
          <div className="stack gap-3" style={{maxWidth:720, position:"relative"}}>
            <Stamp color="var(--ink)">· La carte du parcours ·</Stamp>
            <h1 style={{fontSize:"clamp(56px, 7vw, 124px)", letterSpacing:"-0.025em", lineHeight:0.88}}>
              EXPLOREZ
              <br/>
              <span className="script" style={{
                color:"var(--ink)", fontSize:"0.6em",
                position:"relative", display:"inline-block"
              }}>
                <span style={{position:"relative", zIndex:1}}>à votre rythme</span>
                <span style={{
                  position:"absolute", inset:"-4px -8px", background:"var(--yellow)",
                  clipPath:"polygon(1% 8%, 99% 0%, 100% 86%, 97% 100%, 3% 96%, 0% 12%)",
                  transform:"rotate(-1deg)", zIndex:0
                }}/>
              </span>
            </h1>
            <p className="muted" style={{fontSize:16, maxWidth:540, margin:0, lineHeight:1.5}}>
              Tous les ateliers du parcours. Filtrez par commune, zoomez, cliquez.
            </p>
          </div>
          <div className="row gap-2">
            {[
              ["map",  "Carte", "map"],
              ["list", "Liste", "list"]
            ].map(([v,l,i]) => (
              <button key={v} onClick={() => setView(v)}
                      className={"btn " + (view===v?"btn-primary":"btn-light")}>
                <Icon name={i} size={14}/> {l}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* MAP + sidebar */}
      <section className="container-w">
        <div className="card relative" style={{
          padding:0, overflow:"hidden", borderRadius:16, height:760,
          border:"1.5px solid var(--ink)"
        }}>
          <div style={{display:"flex", height:"100%"}}>
            {/* Sidebar */}
            <div style={{
              width:340, flexShrink:0, display:"flex", flexDirection:"column",
              background:"var(--paper-warm)",
              borderRight:"1.5px solid var(--ink)",
              overflowY:"auto"
            }}>
              <div className="stack gap-5" style={{padding:24, borderBottom:"1px solid var(--line)"}}>
                <div className="search-bar">
                  <Icon name="search" size={16}/>
                  <input value={query} onChange={(e)=>setQuery(e.target.value)}
                         placeholder="Rechercher un lieu, une commune..."/>
                </div>

                <div className="stack gap-3">
                  <div className="stack gap-2">
                    <div className="mono muted">Type de lieu</div>
                    <FilterPills items={typeOptions} value={filters.type}
                                 onChange={(v) => setFilters(f => ({...f, type:v}))}/>
                  </div>
                  <div className="stack gap-2">
                    <div className="mono muted">Commune</div>
                    <FilterPills items={communeOptions.slice(0,6)} value={filters.commune}
                                 onChange={(v) => setFilters(f => ({...f, commune:v}))}/>
                  </div>
                </div>
              </div>

              <div className="stack gap-2" style={{padding:24, flex:1, overflowY:"auto"}}>
                <div className="row between">
                  <span className="mono muted">{venues.length} lieux affichés</span>
                  <button onClick={() => { setFilters({commune:"all",parcours:"all",type:"all"}); setQuery(""); }}
                          className="mono" style={{color:"var(--pink)"}}>Réinitialiser</button>
                </div>
                {venues.map((v,i) => (
                  <button key={v.id} onClick={() => setSelectedVenue(v)}
                          className="row gap-3" style={{
                            padding:"12px 8px",
                            borderBottom:"1px dashed var(--line)",
                            textAlign:"left", width:"100%",
                            background: selectedVenue && selectedVenue.id === v.id ? "var(--paper-2)" : "transparent",
                            borderRadius: 6
                          }}>
                    <span style={{
                      width:26, height:26, borderRadius:"50%",
                      background: colors[i%colors.length],
                      color:"var(--ink)", fontWeight:700, fontSize:11,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      border:"1.5px solid var(--ink)",
                      flexShrink:0
                    }}>{i+1}</span>
                    <div className="stack gap-1" style={{flex:1, minWidth:0}}>
                      <span style={{fontSize:13, fontWeight:600, lineHeight:1.2}}>{v.name}</span>
                      <span className="mono muted">{v.type} · {v.commune}</span>
                    </div>
                  </button>
                ))}
                {venues.length === 0 && (
                  <div className="stack gap-2" style={{padding:24, textAlign:"center"}}>
                    <AsteriskHand color="var(--ink-mute)" size={36} style={{margin:"0 auto"}}/>
                    <span className="muted" style={{fontSize:13}}>Aucun lieu pour ces filtres.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Map area */}
            <div style={{flex:1, position:"relative", minWidth:0}}>
              <div ref={mapRef} className="ol-map" style={{width:"100%", height:"100%"}}/>

              {/* Top floating chip row */}
              <div style={{
                position:"absolute", top:16, left:16, zIndex:5,
                display:"flex", gap:8
              }}>
                <div className="card row gap-2" style={{
                  padding:8, borderRadius:999, border:"1.5px solid var(--ink)", background:"var(--paper-warm)"
                }}>
                  <button onClick={() => setFilters(f => ({...f, type:"all"}))}
                    className={"chip " + (filters.type==="all"?"chip-active":"")}>Tous</button>
                  {typeOptions.slice(1, 5).map(t => (
                    <button key={t.v} onClick={() => setFilters(f => ({...f, type:t.v}))}
                            className={"chip " + (filters.type===t.v?"chip-active":"")}>
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Popup detail when selected */}
              {selectedVenue && (
                <div className="card" style={{
                  position:"absolute", right:24, bottom:24, top:24,
                  width:320, padding:0, borderRadius:14,
                  display:"flex", flexDirection:"column", overflow:"hidden",
                  border:"1.5px solid var(--ink)",
                  boxShadow:"var(--shadow-l)",
                  background:"var(--paper-warm)",
                  zIndex:6
                }}>
                  <div style={{height:180, position:"relative", background:"var(--paper-3)", overflow:"hidden"}}>
                    <img src={D.photo("venue-" + selectedVenue.id, 800, 500)} alt=""
                         style={{width:"100%", height:"100%", objectFit:"cover"}}
                         onError={(e)=>{e.currentTarget.style.display="none";}}/>
                    <button onClick={() => setSelectedVenue(null)} className="btn-pill"
                      style={{position:"absolute", top:12, right:12, width:34, height:34,
                              background:"var(--paper-warm)", color:"var(--ink)"}}>
                      <Icon name="close" size={14}/>
                    </button>
                    <Tape color="pink" style={{top:-10, left:30, width:80, height:18, zIndex:3}}/>
                  </div>
                  <div className="stack gap-3" style={{padding:22, overflowY:"auto", flex:1}}>
                    <div className="row gap-2 wrap">
                      <span className="stamp" style={{background:"var(--pink)", color:"var(--ink)"}}>{selectedVenue.type}</span>
                      <span className="chip"><Icon name="pin" size={11}/> {selectedVenue.commune}</span>
                    </div>
                    <div className="display" style={{fontSize:28, lineHeight:0.95}}>{selectedVenue.name}</div>
                    <p className="muted" style={{margin:0, fontSize:13.5, lineHeight:1.5}}>
                      Ouvert tous les jours du 24 mai au 2 juin, 10h–19h. Trois artistes en résidence.
                    </p>
                    <div className="divider"/>
                    <div className="stack gap-2">
                      <span className="mono muted">Parcours associés</span>
                      {visibleParcours.filter(p => p.venueIds.includes(selectedVenue.id)).slice(0,2).map(p => (
                        <a key={p.id} href={`/parcours/${p.slug}`} className="row gap-2 between"
                           style={{padding:"8px 12px", background:"var(--paper-2)", borderRadius:6}}>
                          <span style={{fontSize:13, fontWeight:600}}>{p.title}</span>
                          <Icon name="arrow-up-right" size={12}/>
                        </a>
                      ))}
                      {visibleParcours.filter(p => p.venueIds.includes(selectedVenue.id)).length === 0 && (
                        <span className="mono muted" style={{fontSize:11}}>Aucun parcours actuellement.</span>
                      )}
                    </div>
                    <div className="row gap-2" style={{marginTop:8}}>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${selectedVenue.lonlat[1]},${selectedVenue.lonlat[0]}`}
                         target="_blank" rel="noopener" className="btn btn-primary btn-sm" style={{flex:1, justifyContent:"center"}}>
                        Y aller
                      </a>
                      <button className="btn btn-light btn-sm" style={{flex:1, justifyContent:"center"}}>
                        Partager
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Legend */}
              <div className="card" style={{
                position:"absolute", left:16, bottom:16, padding:14, borderRadius:12,
                border:"1.5px solid var(--ink)", background:"var(--paper-warm)"
              }}>
                <div className="mono muted" style={{marginBottom:8}}>Légende</div>
                <div className="row gap-3 wrap" style={{maxWidth:280}}>
                  {[
                    ["var(--pink)","Atelier"],
                    ["var(--purple-d)","Galerie"],
                    ["var(--green)","Centre culturel"],
                    ["var(--blue)","Monument"],
                    ["var(--orange)","Lieu rural"]
                  ].map(([c,l]) => (
                    <div key={l} className="row gap-2">
                      <span style={{width:10, height:10, borderRadius:50, background:c, border:"1px solid var(--ink)"}}/>
                      <span style={{fontSize:11}}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* If list view: grid */}
      {view === "list" && (
        <section className="container-w" style={{paddingTop:48}}>
          <h2 style={{fontSize:48, marginBottom:32}}>
            <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:6}}>Tous les lieux</span>
          </h2>
          <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:28}}>
            {venues.map(v => <VenueCard key={v.id} v={v}/>)}
          </div>
        </section>
      )}
    </div>
  );
};

window.ExplorePage = ExplorePage;
